<script setup lang="ts">
import { Head } from '@inertiajs/vue3'
import { ChevronDown, ChevronRight, Mail } from 'lucide-vue-next'
import { ref } from 'vue'

import AppDataTable from '@/components/AppDataTable.vue'
import type { TableColumn } from '@/components/AppDataTable.vue'
import AppPagination from '@/components/AppPagination.vue'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import AppLayout from '@/layouts/AppLayout.vue'
import { formatDateTime } from '@/lib/utils'
import { contactMessagesPath } from '@/routes'
import type { BreadcrumbItem } from '@/types'

interface ContactMessage {
  id: number
  first_name: string
  last_name: string
  email: string
  phone: string
  message: string
  created_at: string
}

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

defineProps<{
  messages: ContactMessage[]
  pagination: PaginationMeta
}>()

const breadcrumbs: BreadcrumbItem[] = [{ title: 'Contact Messages', href: contactMessagesPath() }]

const expandedId = ref<number | null>(null)

function toggleExpand(id: number) {
  expandedId.value = expandedId.value === id ? null : id
}

const tableColumns: TableColumn[] = [
  { key: 'expand', label: '', headerClass: 'w-8' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'received', label: 'Received' },
]
</script>

<template>
  <Head title="Contact Messages" />
  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 overflow-x-auto p-4">
      <div class="flex items-center gap-3">
        <Mail class="text-muted-foreground size-6" />
        <div>
          <h1 class="text-foreground text-xl font-semibold">Contact Messages</h1>
          <p class="text-muted-foreground text-sm">Messages submitted through the marketing website contact form</p>
        </div>
      </div>

      <Card class="border-sidebar-border/70 dark:border-sidebar-border">
        <CardHeader class="pb-3">
          <CardTitle class="text-base">All Messages</CardTitle>
          <CardDescription>
            {{ pagination.total_count }} record{{ pagination.total_count !== 1 ? 's' : '' }}
          </CardDescription>
        </CardHeader>

        <CardContent class="p-0">
          <div v-if="messages.length === 0" class="flex flex-col items-center gap-3 py-16 text-center">
            <Mail class="text-muted-foreground/40 size-10" />
            <p class="text-muted-foreground text-sm">No contact messages yet</p>
          </div>

          <AppDataTable v-else :columns="tableColumns" compact table-fixed>
            <template v-for="message in messages" :key="message.id">
              <tr
                class="border-border cursor-pointer border-b transition-colors select-none"
                :class="expandedId === message.id ? 'bg-muted/60' : 'hover:bg-muted/40'"
                @click="toggleExpand(message.id)"
              >
                <td class="px-3 py-4 text-center">
                  <ChevronDown
                    v-if="expandedId === message.id"
                    class="text-muted-foreground size-4 transition-transform"
                  />
                  <ChevronRight v-else class="text-muted-foreground size-4" />
                </td>
                <td class="px-4 py-4 font-medium">{{ message.first_name }} {{ message.last_name }}</td>
                <td class="text-muted-foreground px-4 py-4">{{ message.email }}</td>
                <td class="text-muted-foreground px-4 py-4">{{ message.phone }}</td>
                <td class="text-muted-foreground px-4 py-4 whitespace-nowrap">
                  {{ formatDateTime(message.created_at) }}
                </td>
              </tr>

              <tr v-if="expandedId === message.id" class="border-border border-b">
                <td colspan="5" class="bg-muted/30 px-6 py-5">
                  <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Message</p>
                  <p class="mt-0.5 text-sm whitespace-pre-wrap">{{ message.message }}</p>
                </td>
              </tr>
            </template>
          </AppDataTable>

          <AppPagination
            v-if="pagination.total_pages > 1"
            :current-page="pagination.current_page"
            :total-pages="pagination.total_pages"
            :total-count="pagination.total_count"
            :per-page="pagination.per_page"
            :base-url="contactMessagesPath()"
          />
        </CardContent>
      </Card>
    </div>
  </AppLayout>
</template>
